import React from "react";
import {
  formatDate,
  stringToColor,
  normalizeLabel,
  getDirection,
} from "../../utils/messageUtils";

const MODAL_STYLES = `
  .message-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(30, 25, 15, 0.45);
    z-index: var(--z-modal);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-4);
    animation: backdropFadeIn 0.2s ease-out;
  }

  .message-modal-content {
    position: relative;
    background: var(--paper-card);
    border: 1px solid var(--paper-300);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-paper-lg);
    max-width: 560px;
    width: 100%;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    animation: modalScaleIn 0.25s var(--ease-in-out);
  }

  .message-modal-tape {
    position: absolute;
    top: -9px;
    left: 50%;
    width: 96px;
    height: 22px;
    transform: translateX(-50%) rotate(-1.5deg);
    background: rgba(118, 164, 187, 0.28);
    border-left: 1px dashed rgba(255, 255, 255, 0.7);
    border-right: 1px dashed rgba(255, 255, 255, 0.7);
  }

  .message-modal-header {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-6) var(--space-6) var(--space-4);
  }

  .message-modal-avatar {
    width: 56px;
    height: 56px;
    border-radius: var(--radius-full);
    display: grid;
    place-items: center;
    color: #14212e;
    font-weight: var(--font-bold);
    font-size: 1.5rem;
    border: 2px solid rgba(255, 255, 255, 0.75);
    box-shadow: var(--shadow-paper-sm);
    flex-shrink: 0;
  }

  .message-modal-info {
    flex: 1;
    min-width: 0;
  }

  .message-modal-name {
    font-family: var(--font-accent);
    font-weight: 600;
    font-size: clamp(1.6rem, 3vw, 2rem);
    line-height: 1.1;
    color: var(--ink-900);
    margin: 0 0 var(--space-1) 0;
  }

  .message-modal-location {
    font-size: var(--text-sm);
    color: var(--ink-500);
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .message-modal-badge {
    padding: 2px 12px;
    border-radius: var(--radius-full);
    background: var(--paper-200);
    border: 1px solid var(--paper-300);
    color: var(--ink-700);
    font-weight: var(--font-medium);
  }

  .message-modal-close {
    position: absolute;
    top: var(--space-3);
    inset-inline-end: var(--space-3);
    width: 40px;
    height: 40px;
    border-radius: var(--radius-full);
    background: var(--paper-200);
    border: 1px solid var(--paper-300);
    color: var(--ink-700);
    font-size: 1.3rem;
    cursor: pointer;
    display: grid;
    place-items: center;
  }

  .message-modal-close:hover {
    background: var(--paper-300);
    transform: scale(1.06);
  }

  .message-modal-body {
    padding: 0 var(--space-6) var(--space-6);
    overflow-y: auto;
    flex: 1;
  }

  .message-modal-message {
    font-size: var(--text-lg);
    line-height: var(--leading-relaxed);
    color: var(--ink-700);
    white-space: pre-line;
    unicode-bidi: plaintext;
    margin: 0;
  }

  .message-modal-date {
    margin-top: var(--space-5);
    padding-top: var(--space-3);
    border-top: 1px dashed var(--paper-300);
    font-size: var(--text-xs);
    color: var(--ink-500);
  }

  @keyframes backdropFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes modalScaleIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(14px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (max-width: 600px) {
    .message-modal-backdrop {
      padding: var(--space-2);
      align-items: flex-end;
    }

    .message-modal-content {
      max-width: 100%;
      max-height: 90vh;
      border-radius: var(--radius-xl) var(--radius-xl) 0 0;
      animation: modalSlideUp 0.3s var(--ease-in-out);
    }

    .message-modal-header {
      padding: var(--space-5) var(--space-5) var(--space-3);
    }

    .message-modal-body {
      padding: 0 var(--space-5) var(--space-5);
    }

    .message-modal-avatar {
      width: 48px;
      height: 48px;
      font-size: 1.25rem;
    }
  }

  @keyframes modalSlideUp {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const MessageModal = ({ message, onClose }) => {
  const modalRef = React.useRef(null);

  // Handle Escape key
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Focus trap
  React.useEffect(() => {
    modalRef.current?.focus();
  }, []);

  if (!message) return null;

  const countryLabel = normalizeLabel(message.country);
  const cityLabel = normalizeLabel(message.city);
  const avatarColor = stringToColor(
    message.name || countryLabel || "Guest"
  );
  const avatarInitial = (message.name || countryLabel || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      <style>{MODAL_STYLES}</style>
      <div
        className="message-modal-backdrop"
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="message-modal-title"
      >
        <div className="message-modal-content" ref={modalRef} tabIndex={-1}>
          <div className="message-modal-tape" aria-hidden="true" />
          <button
            className="message-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>

          <div
            className="message-modal-header"
            dir={getDirection(message.name)}
          >
            <div
              className="message-modal-avatar"
              style={{ background: avatarColor }}
              aria-hidden="true"
            >
              {avatarInitial}
            </div>
            <div className="message-modal-info">
              <h2 className="message-modal-name" id="message-modal-title">
                {message.name || "Anonymous"}
              </h2>
              <div className="message-modal-location">
                {countryLabel && (
                  <span className="message-modal-badge">{countryLabel}</span>
                )}
                {cityLabel && <span>{cityLabel}</span>}
                {!countryLabel && !cityLabel && <span>Guest</span>}
              </div>
            </div>
          </div>

          <div className="message-modal-body">
            <p
              className="message-modal-message"
              dir={getDirection(message.message)}
            >
              {message.message}
            </p>
            {message.timestamp && (
              <div className="message-modal-date">
                {formatDate(message.timestamp, true)}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MessageModal;
